import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Headphones, Play, Pause, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoadBriefRecorder, type LoadBriefRecording } from "@/hooks/useLoadBriefRecorder";
import { useMicVolumeMeter } from "@/hooks/useMicVolumeMeter";

export interface LRDirectLoadBriefValue {
  blob: Blob;
  mime: string;
  durationSec: number;
}

interface LRDirectLoadBriefCardProps {
  value: LRDirectLoadBriefValue | null;
  onChange: (v: LRDirectLoadBriefValue | null) => void;
}

/**
 * LR Direct Load Brief authoring (record-only).
 * ISOLATED clone of the marketplace card — shares only the browser-level
 * recorder/volume hooks. Data path (bucket + direct_loads columns) is
 * LR-Direct-specific and lives in LRDirectPostPage.
 */
export function LRDirectLoadBriefCard({ value, onChange }: LRDirectLoadBriefCardProps) {
  const rec = useLoadBriefRecorder();
  const level = useMicVolumeMeter(rec.stream, rec.state === "recording");

  useEffect(() => {
    if (rec.recording) {
      onChange({
        blob: rec.recording.blob,
        mime: rec.recording.mime,
        durationSec: rec.recording.durationSec,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.recording]);

  const handleDiscard = () => {
    rec.reset();
    onChange(null);
  };

  const fmt = (s: number) => `${Math.floor(s).toString().padStart(2, "0")}s`;
  const cap = rec.maxSec;
  const progressPct = Math.min(100, (rec.elapsedSec / cap) * 100);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-border bg-muted/30 px-3 py-2">
        <Headphones className="h-3.5 w-3.5 text-brand-teal-deep" />
        <h3 className="text-xs font-semibold text-foreground">Load Brief (Optional)</h3>
        <span className="ml-auto text-[10px] text-muted-foreground">Up to {cap}s</span>
      </div>

      <div className="p-4 space-y-3">
        {rec.state === "idle" && !value && (
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Record a short note carriers can listen to before they call. Origin, equipment, special handling — whatever helps qualify the call.
              </p>
              <p className="flex items-center gap-1 text-[11px] text-amber-600 leading-relaxed">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                Keep it general. Don't share pickup numbers, PO numbers, or other details you'd only give to a booked carrier.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={rec.start}
              className="shrink-0 gap-1.5 bg-brand-teal text-white hover:bg-brand-teal-deep"
            >
              <Mic className="h-3.5 w-3.5" />
              Record
            </Button>
          </div>
        )}

        {rec.state === "requesting" && (
          <p className="text-[11px] text-muted-foreground">Requesting microphone access…</p>
        )}

        {rec.state === "recording" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
                </span>
                <span className="text-xs font-semibold text-foreground tabular-nums">
                  {fmt(rec.elapsedSec)} / {fmt(cap)}
                </span>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={rec.stop} className="gap-1.5">
                <Square className="h-3.5 w-3.5" />
                Stop
              </Button>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-brand-teal transition-all duration-150"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="flex items-end gap-0.5 h-4">
              {Array.from({ length: 24 }).map((_, i) => {
                const threshold = (i + 1) / 24;
                const active = level >= threshold * 0.9;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-sm transition-colors ${active ? "bg-brand-teal-deep" : "bg-muted"}`}
                    style={{ height: `${20 + i * 2}%` }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {rec.state === "recorded" && rec.recording && (
          <div className="space-y-2">
            <BriefPreviewPlayer recording={rec.recording} />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">
                Brief attached • {rec.recording.durationSec}s
              </span>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={handleDiscard} className="gap-1.5">
                  <Trash2 className="h-3.5 w-3.5" />
                  Discard
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    rec.reset();
                    rec.start();
                  }}
                  className="gap-1.5 bg-brand-teal text-white hover:bg-brand-teal-deep"
                >
                  <Mic className="h-3.5 w-3.5" />
                  Re-record
                </Button>
              </div>
            </div>
          </div>
        )}

        {rec.state === "error" && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-destructive">{rec.error || "Couldn't access microphone."}</p>
            <Button type="button" size="sm" variant="outline" onClick={rec.reset}>
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function BriefPreviewPlayer({ recording }: { recording: LoadBriefRecording }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState<number>(recording.durationSec || 0);

  useEffect(() => {
    setPlaying(false);
    setT(0);
    setDur(recording.durationSec || 0);
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
  }, [recording.url, recording.durationSec]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => setPlaying(false));
  };

  const pct = dur > 0 ? Math.min(100, (t / dur) * 100) : 0;

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={toggle}
        className="h-7 w-7 p-0 shrink-0"
        aria-label={playing ? "Pause preview" : "Play preview"}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </Button>
      <div className="flex-1 min-w-[40px] h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-brand-teal transition-[width] duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
        {Math.floor(t)}s / {dur || 0}s
      </span>
      <audio
        ref={audioRef}
        src={recording.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setT(0);
        }}
        onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d) && d > 0) setDur(Math.round(d));
        }}
        className="hidden"
      />
    </div>
  );
}
